import type { NavigationItem } from '@src/tokens/navigationPaths';
import { IconName, type DeviceType } from '@talos/kyoko';
import { matchPath } from 'react-router-dom';

const MOBILE_SUPPORTED_SECTION_PATHS = new Set(['/trading']);

export const DEVICE_TYPE_ICONS: Record<DeviceType, IconName> = {
  mobile: IconName.DeviceMobile,
  tablet: IconName.DeviceTablet,
  laptop: IconName.DeviceLaptop,
  desktop: IconName.DeviceDesktop,
  widescreen: IconName.DeviceDesktopWide,
  all: IconName.Globe,
};

type MenuRow =
  | {
      type: 'group';
      key: string;
      label: string;
    }
  | {
      type: 'item';
      key: string;
      item: NavigationItem;
    };

export function isActivePath(path: string | undefined, pathname: string) {
  return path != null && matchPath({ path, end: false }, pathname) != null;
}

export function getMenuRows(section: NavigationItem | undefined): MenuRow[] {
  const rows: MenuRow[] = [];

  for (const child of section?.children ?? []) {
    const childRows = getChildRows(child);
    if (childRows.length === 0) {
      continue;
    }

    if (child.label != null && hasLabeledChildren(child)) {
      rows.push({ type: 'group', key: child.path ?? child.label, label: child.label });
    }
    rows.push(...childRows);
  }

  return rows;
}

export function isMobileSupportedSection(item: NavigationItem) {
  return item.path != null && MOBILE_SUPPORTED_SECTION_PATHS.has(item.path);
}

function getChildRows(item: NavigationItem): MenuRow[] {
  const children = getNavigationChildren(item);
  if (item.label != null && item.path != null && !children.some(child => child.label != null)) {
    return [{ type: 'item', key: item.id ?? item.path, item }];
  }

  return children.flatMap(getChildRows);
}

function hasLabeledChildren(item: NavigationItem) {
  return getNavigationChildren(item).some(child => child.label != null);
}

function getNavigationChildren(item: NavigationItem): NavigationItem[] {
  return (item.children ?? []) as NavigationItem[];
}
